import React from 'react';
import LegalDocumentLayout from './LegalDocumentLayout';

const PrivacyPolicy = () => (
  <LegalDocumentLayout title="Privacy Policy" subtitle="Version 1.0 · Effective upon acceptance · Governed by the laws of the State of Delaware, USA">
    <p>
      This Privacy Policy describes how Cloud Guidant Inc. ("Cloud Guidant," "we," or "us") collects, processes,
      stores, and uses information in connection with the Axaris platform ("Services"). As Axaris is a live,
      continuously evolving platform, the data processing activities described in this Policy may expand or change
      as new features and services are introduced. This Policy is updated to reflect material changes and
      incorporates the Living Platform terms set out in the Axaris Terms of Use, which apply equally to this
      Policy. This Policy is incorporated into the Axaris Terms of Use.
    </p>
    <p>
      Contact: <a href="mailto:privacy@cloudguidant.com">privacy@cloudguidant.com</a>
    </p>

    <h2>1. Who We Are</h2>
    <p>
      Cloud Guidant Inc. is a Delaware corporation operating the Axaris AI-powered Predictive Enterprise Risk
      Intelligence platform. We are a Data Processor for our Customers' data and a Data Controller for our own
      operational data.
    </p>

    <h2>2. Living Platform — Data Processing May Evolve</h2>
    <p>
      Axaris is a live platform. New features, modules, connectors, AI capabilities, and data sources are added
      continuously. The categories set out in this Section reflect our current data processing activities as of
      the effective date of this Policy. As the Services evolve and new features are introduced, additional
      categories of data may be processed. We will update this Policy to reflect material additions; continued use
      of the Services following any update constitutes acceptance. The catch-all entry at the end of this Section
      covers data processed in connection with new features introduced after the effective date, and forms part of
      this Policy from the moment any such feature is made available.
    </p>

    <h2>3. Data We Collect</h2>
    <p>
      <strong>Account and identity data.</strong> Names, email addresses, job titles, and authentication data via
      Microsoft Entra. Stored in a globally-hosted US Azure identity layer. No passwords stored. Used for
      authentication, access management, and Service delivery.
    </p>
    <p>
      <strong>Risk intelligence data.</strong> Security findings, vulnerability data, risk scores, and assessment
      outputs from Customer environments. Stored exclusively in the Customer's designated data residency region.
      Unified cross-region reporting via query-time aggregation only; not persisted outside origin region.
    </p>
    <p>
      <strong>Connector data.</strong> Data from Third-Party Platforms (including without limitation Microsoft
      Azure, AWS, Wiz, ServiceNow, Jira, and any other platforms connected from time to time) retrieved via
      Connectors enabled by the Customer. Processed within the Customer's designated region. The list of supported
      Connectors is live and continuously updated.
    </p>
    <p>
      <strong>Uploaded content and AI assessment data.</strong> Documents, policies, questionnaire responses, and
      materials uploaded for any current or future AI assessment feature. Processed within the Customer's region.
      Used to generate AI Outputs and Derived Data. We recommend minimising PII in Uploaded Content.
    </p>
    <p>
      <strong>Dark web intelligence data.</strong> Credential exposure records, threat actor profiles, data breach
      indicators, and ransomware activity sourced from third-party intelligence providers for AXARIS-D and any
      future threat monitoring features. Accuracy, completeness, and timeliness not guaranteed. Processed solely
      to deliver threat intelligence outputs.
    </p>
    <p>
      <strong>Workforce intelligence data.</strong> FTE, skill area, and financial exposure data for the Workforce
      Intelligence module and any future workforce analysis features. Stored in the Customer's designated region.
    </p>
    <p>
      <strong>Usage and operational data.</strong> Platform usage logs, access records, and diagnostics used to
      operate, secure, and improve the Services. May expand as new features are introduced.
    </p>
    <p>
      <strong>Future feature data.</strong> Data processed in connection with features, modules, or connectors
      introduced after the effective date of this Policy. Such processing will be of the same general nature as
      described above (platform operation, AI analysis, security monitoring, reporting) and will be subject to the
      same protections set out in this Policy. Material new processing activities that differ in nature from those
      listed above will be described in an updated version of this Policy upon introduction.
    </p>

    <h2>4. How We Use Data</h2>
    <p>
      We use data to: provide and operate the Services in their current and any future form; generate AI Outputs
      across all current and future modules and features; monitor security threats; authenticate users; fulfil
      legal obligations; and improve and develop our AI models and platform. We may use Derived Data (aggregated,
      anonymised, de-identified) for any purpose including AI training, benchmarking, and product development,
      without restriction or notice.
    </p>

    <h2>5. Data Residency and Architecture</h2>
    <p>
      Risk data, Connector data, and Uploaded Content are stored in the Customer's designated region. AI
      processing is region-aligned where available, with dedicated in-region AI deployment options available for
      Customers with strict jurisdictional requirements. Cloud Guidant makes no warranty that AI processing will
      be confined to any specific region and shall not be liable for any regulatory consequence arising from AI
      processing location. Query-time aggregation only for unified reporting. Schema-level tenant isolation by
      architecture. Region availability may change as the platform evolves; Cloud Guidant will use reasonable
      efforts to migrate data if a region is deprecated.
    </p>

    <h2>6. Data Sharing</h2>
    <p>We do not sell Personal Data. We share data only with:</p>
    <ul>
      <li><strong>Microsoft Azure</strong> — primary infrastructure; all platform components run on Azure</li>
      <li><strong>Proprietary threat intelligence partners</strong> — identities withheld for IP reasons; bound to equivalent data protection standards</li>
      <li><strong>Future sub-processors</strong> engaged in connection with new features and services, for which Customer advance authorisation is granted under the DPA</li>
      <li><strong>Third-Party Platforms via Connectors</strong> — at Customer direction; operate under their own privacy policies; not our sub-processors</li>
      <li><strong>Legal and regulatory authorities</strong> — where required by applicable law</li>
    </ul>

    <h2>7. Data Retention</h2>
    <p>
      Account data: retained for subscription term plus 180 days post-termination. Risk intelligence, Connector,
      and Uploaded Content data: retained in Customer's region for subscription term plus 180 days. Dark web
      intelligence snapshots: continuously refreshed; historical records deleted per standard schedule. Derived
      Data: retained indefinitely as it contains no PII. Retention schedules may change as the platform evolves.
    </p>

    <h2>8. Security</h2>
    <p>
      AES-256 at rest, TLS 1.2+ in transit, schema-level tenant isolation, RBAC, Microsoft Entra authentication,
      no passwords stored. Security measures are reviewed and updated periodically as the platform evolves. We do
      not warrant that all incidents can be prevented.
    </p>

    <h2>9. Individual Rights</h2>
    <p>
      Individuals whose data is processed by Cloud Guidant as Data Processor should direct requests to the
      relevant Customer (the Data Controller). Where Cloud Guidant is a Data Controller (account data of Customer
      contacts), rights requests can be sent to{' '}
      <a href="mailto:privacy@cloudguidant.com">privacy@cloudguidant.com</a>. Rights include: access, correction,
      erasure (subject to retention obligations), restriction, and portability, where applicable under law. For
      Dark Web Intelligence Data, full correction or erasure cannot be guaranteed due to third-party sourcing.
    </p>

    <h2>10. California Residents (CCPA/CPRA)</h2>
    <p>
      California residents may: know what Personal Data is collected; request deletion (subject to exceptions);
      opt out of sale or sharing (we do not sell or share for advertising); and exercise rights without
      discrimination. Contact: <a href="mailto:privacy@cloudguidant.com">privacy@cloudguidant.com</a>.
    </p>

    <h2>11. International Users (EEA/UK)</h2>
    <p>
      Account data is processed in the US; risk data in the Customer's chosen region. Where required, we implement
      Standard Contractual Clauses or other appropriate safeguards. The DPA contains further details for EU/UK
      Customers.
    </p>

    <h2>12. AI and Automated Processing</h2>
    <p>
      AI Outputs are probabilistic and advisory. No AI Output constitutes automated decision-making with legal or
      similarly significant effects on individuals under GDPR Article 22 or equivalent. All outputs require human
      review. AI model behaviour may change as models are updated or retrained; this is a feature of the live
      platform and not a material change to data processing for the purposes of this Policy.
    </p>

    <h2>13. Cookies and Tracking</h2>
    <p>
      Essential cookies and session tokens for authentication and platform operation only. No third-party
      advertising cookies or cross-site tracking. Usage analytics for internal improvement only. Cookie use may
      change as new features are introduced.
    </p>

    <h2>14. Children's Data</h2>
    <p>
      Services not directed at individuals under 18. We do not knowingly collect minors' data. We will delete it
      promptly if identified.
    </p>

    <h2>15. Changes to This Policy</h2>
    <p>
      We may update this Policy at any time by posting a revised version within the platform. No separate notice
      is required. Continued use of the Services constitutes acceptance of this Policy as updated. You are
      responsible for reviewing this Policy periodically. Cloud Guidant has no liability arising from the
      Customer's failure to review updated versions.
    </p>

    <h2>16. Contact</h2>
    <p>
      Cloud Guidant Inc. | Privacy Team:{' '}
      <a href="mailto:privacy@cloudguidant.com">privacy@cloudguidant.com</a> | cloudguidant.com | Wilmington,
      Delaware, USA
    </p>
  </LegalDocumentLayout>
);

export default PrivacyPolicy;
